import { readCondition } from '@seen/core/observation-logic/documentUnits'
import { describeCondition } from '@seen/core/observation-logic/evaluate'
import { cn } from '@seen/ui'
import {
  NodeViewContent,
  type NodeViewProps,
  NodeViewWrapper,
  useEditorState,
} from '@tiptap/react'
import type { JSX } from 'react'
import { ConditionalBranchIcon } from '#src/components/rich-text-editor/conditional-display/ConditionalBranchIcon.tsx'
import { CONDITIONAL_DISPLAY_KEY } from '#src/components/rich-text-editor/conditional-display/ConditionalDisplayExtension.ts'
import type { ConditionalSpanOptions } from '#src/components/rich-text-editor/conditional-display/ConditionalSpanNode.ts'

// A conditional span. Live it is just its content (hidden by the display
// plugin when its condition fails); in a template it is outlined, the
// branch glyph at its start wording its condition and opening its
// configuration.
export function ConditionalSpanView(props: NodeViewProps): JSX.Element {
  const { isTemplate } = props.extension.options as ConditionalSpanOptions
  const id = props.node.attrs.id as string | null
  const condition = readCondition(props.node.attrs.showWhen)
  const label = props.node.attrs.label as string | null
  const describe = useEditorState({
    editor: props.editor,
    selector: ({ editor }) =>
      CONDITIONAL_DISPLAY_KEY.getState(editor.state)?.describe,
  })
  const cycle = useEditorState({
    editor: props.editor,
    selector: ({ editor }) =>
      !!id &&
      !!CONDITIONAL_DISPLAY_KEY.getState(editor.state)?.result.cycles.includes(
        `section:${id}`
      ),
  })

  if (!isTemplate) {
    return (
      <NodeViewWrapper as="span" data-conditional-span="">
        <NodeViewContent<'span'> as="span" />
      </NodeViewWrapper>
    )
  }

  const text = condition ? (describe ?? describeCondition)(condition) : 'always'
  const title = `${label ? `${label} · ` : ''}Shows when: ${text}${cycle ? ' (refers to itself)' : ''}`
  return (
    <NodeViewWrapper
      as="span"
      data-conditional-span=""
      className={cn(
        'rounded-xs outline-dashed outline-1 outline-offset-1',
        cycle ? 'outline-red-400' : 'outline-gray-450'
      )}
    >
      <button
        type="button"
        contentEditable={false}
        data-condition-chip=""
        title={title}
        aria-label={title}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => {
          const pos = props.getPos()
          if (typeof pos === 'number') {
            props.editor.commands.openNodeConfig(pos)
          }
        }}
        className={cn(
          'mr-0.5 inline-flex items-center align-middle',
          cycle ? 'text-red-600' : 'text-muted-foreground hover:text-foreground'
        )}
      >
        <ConditionalBranchIcon />
      </button>
      <NodeViewContent<'span'> as="span" />
    </NodeViewWrapper>
  )
}
